import { type FullAirHandlerUnit } from "@eisox/api/core-api";

import { compareAirHandlerUnitNames } from "./air-handler-unit.helper";
import { getFullAirHandlerUnitsByGroup } from "./group.helper";

type EditableAssociationKind = "airHandlerUnit" | "group";

interface EditableAirHandlerUnitRoomAssociation {
  key: string;
  kind: EditableAssociationKind;
  name: string;
  airHandlerUnitIds: string[];
  roomIds: string[];
  initialRoomIds: string[];
  floorIds: string[];
}

const normalizeRoomIds = (roomIds?: string[]): string[] =>
  [...new Set(roomIds ?? [])].sort((a, b) => a.localeCompare(b));

const areRoomIdsEqual = (left: string[], right: string[]) =>
  left.length === right.length && left.every((roomId, index) => roomId === right[index]);

const buildEditableAirHandlerUnitRoomAssociations = (
  airHandlerUnits: FullAirHandlerUnit[],
): EditableAirHandlerUnitRoomAssociation[] => {
  const byGroup = getFullAirHandlerUnitsByGroup(airHandlerUnits);

  return Object.values(byGroup)
    .filter(
      (groupedAirHandlerUnits): groupedAirHandlerUnits is [FullAirHandlerUnit, ...FullAirHandlerUnit[]] =>
        groupedAirHandlerUnits.length > 0,
    )
    .map(groupedAirHandlerUnits => {
      const firstAirHandlerUnit = groupedAirHandlerUnits[0];
      const kind: EditableAssociationKind = firstAirHandlerUnit.group ? "group" : "airHandlerUnit";
      const initialRoomIds = normalizeRoomIds(
        groupedAirHandlerUnits.flatMap(airHandlerUnit => airHandlerUnit.roomIds ?? []),
      );
      const floorIds = [
        ...new Set(groupedAirHandlerUnits.flatMap(airHandlerUnit => airHandlerUnit.floor?.id ?? [])),
      ].sort((a, b) => a.localeCompare(b));

      return {
        key: `${kind}:${firstAirHandlerUnit.group ?? firstAirHandlerUnit.id}`,
        kind,
        name: firstAirHandlerUnit.group ?? firstAirHandlerUnit.name,
        airHandlerUnitIds: groupedAirHandlerUnits.map(airHandlerUnit => airHandlerUnit.id),
        roomIds: initialRoomIds,
        initialRoomIds,
        floorIds,
      } satisfies EditableAirHandlerUnitRoomAssociation;
    })
    .sort((left, right) => compareAirHandlerUnitNames(left.name, right.name));
};

const isAssociationEdited = (association: EditableAirHandlerUnitRoomAssociation): boolean =>
  !areRoomIdsEqual(normalizeRoomIds(association.roomIds), association.initialRoomIds);

interface UpdateAirHandlerUnitRoomAssociation {
  airHandlerUnitIds: string[];
  roomIds: string[];
}

const buildUpdateAirHandlerUnitRoomAssociationsRequestBody = (
  associations: EditableAirHandlerUnitRoomAssociation[],
): UpdateAirHandlerUnitRoomAssociation[] =>
  associations.filter(isAssociationEdited).map(association => ({
    airHandlerUnitIds: association.airHandlerUnitIds,
    roomIds: normalizeRoomIds(association.roomIds),
  }));

const applyRoomAssociationsToAirHandlerUnits = (
  airHandlerUnits: FullAirHandlerUnit[],
  body: UpdateAirHandlerUnitRoomAssociation[],
): FullAirHandlerUnit[] => {
  const nextAirHandlerUnits = [...airHandlerUnits];

  body.forEach(update => {
    update.airHandlerUnitIds.forEach(airHandlerUnitId => {
      const index = nextAirHandlerUnits.findIndex(airHandlerUnit => airHandlerUnit.id === airHandlerUnitId);
      const currentAirHandlerUnit = nextAirHandlerUnits[index];
      if (currentAirHandlerUnit) {
        nextAirHandlerUnits[index] = {
          ...currentAirHandlerUnit,
          roomIds: normalizeRoomIds(update.roomIds),
        };
      }
    });
  });

  return nextAirHandlerUnits;
};

export {
  applyRoomAssociationsToAirHandlerUnits,
  buildEditableAirHandlerUnitRoomAssociations,
  buildUpdateAirHandlerUnitRoomAssociationsRequestBody,
  isAssociationEdited,
  normalizeRoomIds,
};
export type { EditableAirHandlerUnitRoomAssociation, UpdateAirHandlerUnitRoomAssociation };
